import { useMutation, useQueryClient } from "@tanstack/react-query";
import z from "zod/v4";

import { AFFILIATE_ACCRUALS_QUERY_KEY } from "@/contexts/queries/affiliate-accruals";
import { env } from "@/env";
import { authenticatedRequest } from "@/utils/signature-auth";

import { useIsAuthReady, useWriteDexSdk } from "../write-dex-sdk-context";

/**
 * Elect points or USD commission.
 *
 * The request carries the mode and nothing else — the backend takes the address from the session.
 * That is not belt-and-braces: the election is one-time and irreversible, so an address supplied
 * by the caller would let anyone spend anyone else's single choice.
 */
const ElectResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    affiliate_account: z.string(),
    mode: z.string(),
    /** First UTC day it applies: the day after the election was recorded. */
    effective_from: z.string(),
    /** What the affiliate earns until then. */
    mode_until_effective: z.string(),
  }),
});

export type ElectRewardsModeResult = z.infer<typeof ElectResponseSchema>["data"];

export function useElectRewardsMode() {
  const queryClient = useQueryClient();
  const { getAuthHeaders, subaccountAddress } = useWriteDexSdk();
  const isAuthReady = useIsAuthReady();

  return useMutation({
    mutationFn: async (mode: "points" | "usd"): Promise<ElectRewardsModeResult> => {
      const { data: body } = await authenticatedRequest.post({
        schema: ElectResponseSchema,
        url: `${env.NEXT_PUBLIC_API_URL}/api/affiliates/rewards-mode`,
        body: { mode },
        getAuthHeaders,
        subaccountAddress,
      });
      return body.data;
    },
    onSuccess: () => {
      // The accruals response carries `mode`, and the card reads it to decide which side is
      // active. Without this the affiliate elects and the screen keeps showing the old mode until
      // something else happens to refetch.
      void queryClient.invalidateQueries({ queryKey: [AFFILIATE_ACCRUALS_QUERY_KEY] });
    },
    // Referenced so the mutation is recreated when the wallet finishes hydrating, matching how the
    // queries key on it. Without it a mutation captured before hydration keeps a stale signer.
    meta: { isAuthReady },
  });
}
